import React, { FC } from 'react';
import { Dialog, DialogContent, List, ListItem, IconButton, Typography } from '@material-ui/core';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import styled from 'styled-components';

const ModalDescription = styled.p`
    color: var(--text-muted);
    margin-bottom: var(--spacer-M);
    text-align: center;
`;

const StyledListItem = styled(ListItem)`
    && {
        padding: var(--spacer-S);
        border: var(--border-thin) solid var(--appLigthGrey);
        margin-bottom: var(--spacer-XS);
        border-radius: var(--spacer-XXS);
        transition: all 0.2s;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: var(--spacer-S);

        &:hover {
            background-color: var(--bg-hover-light);
            border-color: var(--appBlue);
        }
    }
`;

const BusinessName = styled(Typography)`
    && {
        font-weight: 600;
        font-size: 1.1rem;
        margin-bottom: var(--spacer-XXS);
    }
`;

const ModalHeader = styled.div`
    position: relative;
    padding: var(--spacer-M);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CloseButton = styled(IconButton)`
    && {
        position: absolute;
        top: var(--spacer-XS);
        right: var(--spacer-XS);
    }
`;

const ModalTitle = styled(Typography)`
    && {
        font-size: 1.25rem;
        font-weight: 600;
    }
`;

const BusinessLogoWrapper = styled.div`
    width: 60px;
    height: 60px;
    flex-shrink: 0;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: var(--spacer-XXS);
    }
`;

const BusinessLogoPlaceholder = styled.div`
    width: 60px;
    height: 60px;
    background-color: var(--bg-light);
    border-radius: var(--spacer-XXS);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: var(--text-muted);
    font-weight: 600;
    font-size: 1.5rem;
`;

export interface Business {
    id: string;
    name: string;
    logo?: string;
}

interface BusinessSelectionModalProps {
    isOpen: boolean;
    businesses: Business[];
    onSelectBusiness: (businessId: string) => void;
    onClose?: () => void;
    title?: string;
}

export const BusinessSelectionModal: FC<BusinessSelectionModalProps> = ({
    isOpen,
    businesses,
    onSelectBusiness,
    onClose,
    title,
}) => {
    const t = useTranslations();

    return (
        <Dialog
            open={isOpen}
            maxWidth="sm"
            fullWidth
            onClose={onClose}
        >
            <ModalHeader>
                <ModalTitle>{title ?? t(TK.selectYourBusiness)}</ModalTitle>
                {onClose && (
                    <CloseButton
                        onClick={onClose}
                        aria-label={t(TK.close)}
                    >
                        ✕
                    </CloseButton>
                )}
            </ModalHeader>
            <DialogContent>
                <ModalDescription>
                    {t(TK.youHaveMultipleBusinesses)}<br />
                    {t(TK.pleaseSelectOne)}
                </ModalDescription>
                <List>
                    {businesses.map((business) => (
                        <StyledListItem
                            key={business.id}
                            button
                            onClick={() => onSelectBusiness(business.id)}
                        >
                            {business.logo ? (
                                <BusinessLogoWrapper>
                                    <img src={business.logo} alt={business.name} />
                                </BusinessLogoWrapper>
                            ) : (
                                <BusinessLogoPlaceholder>
                                    {business.name.charAt(0).toUpperCase()}
                                </BusinessLogoPlaceholder>
                            )}
                            <BusinessName>{business.name}</BusinessName>
                        </StyledListItem>
                    ))}
                </List>
            </DialogContent>
        </Dialog>
    );
};
